import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { WAREHOUSES } from 'services/consts';

import { getPickupLocationCountries, setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import * as S from '../QuickShipment.styled';

const PickupLocation = ({ nextStep, setPickupLocationCreated }, ref) => {
	const { Country } = useLocale();
	const user = useSelector((state) => state.user);
	const { planName = null, localization: { allowedCountries: allowedCountriesStr } = {} } = user;

	const allowedCountries = useMemo(
		() => (allowedCountriesStr ? [Country, ...allowedCountriesStr.split(',')] : [Country]),
		[allowedCountriesStr]
	);

	const defaultColSpan = {
		md: 6
	};

	const axios = useAxiosPrivate();
	const dispatch = useDispatch();

	const formMethods = useFormContext();

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(WAREHOUSES.UPDATE_PICKUP_LOCATION, {
				address: data.senderAddress,
				email: data.senderEmail,
				senderName: data.senderName,
				mobileNumber: data.senderMobileNumber,
				warehouseName: data.warehouseName
			});

			if (response.status === 200) {
				dispatch(getPickupLocationCountries({ invalidate: true }));
				if (response.data?.ID) {
					setValue('warehouseID', response.data?.ID);
					dispatch(
						setUserDetails({
							generalSettings: {
								...user.generalSettings,
								defaultWarehouse: response.data.ID
							}
						})
					);
				}
				nextStep();
				setPickupLocationCreated(true);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			async save() {
				await handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		}),
		[handleSubmit, onSubmit]
	);
	return (
		<S.PickupLocationWrapper>
			<S.CreateShipmentContent>
				<S.Title variant="p2" fontWeight="500">
					pickupLocationName
				</S.Title>
				<S.CreateShipmentContentWrapper>
					<FormItem
						colSpan={defaultColSpan}
						label="pickupLocationName"
						isLoading={isSubmitting}
						required
						toolTip="pickupLocation.pickupLocationNameTooltip">
						<StringInput name="warehouseName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isSubmitting} required>
						<StringInput name="senderName" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="email" isLoading={isSubmitting} required>
						<StringInput name="senderEmail" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isSubmitting} required>
						<PhoneNumber
							name="senderMobileNumber"
							value={watch('senderMobileNumber')}
							onChange={(value) =>
								setValue('senderMobileNumber', value, { shouldValidate: true, shouldDirty: true })
							}
						/>
					</FormItem>
					<AddressGroupV2
						formFieldName="senderAddress"
						isLoading={isSubmitting}
						restrictions={
							planName !== 'EnterprisePackage'
								? {
										country: {
											isNotDifferent: planName === 'FreePackage',
											code: planName === 'FreePackage' ? Country : allowedCountries
										}
									}
								: null
						}
					/>
				</S.CreateShipmentContentWrapper>
			</S.CreateShipmentContent>
		</S.PickupLocationWrapper>
	);
};

export default forwardRef(PickupLocation);
